@if (domainNotConnected()) {
  <!-- A foreign host (custom domain) the server refused: nothing is broken —
       the DOMAIN is not connected. Dedicated notice, never the generic failure page. -->
  <app-domain-not-connected />
} @else if (configLoadFailed()) {
  <!-- Tenant config never loaded (after retries) — no storage URLs, languages, or brand
       identity to run on, so the shell must not boot. Honest error page instead. -->
  <app-config-error />
} @else {
<!-- The chrome outside the layout — the banners here and the connectivity/update bars below — goes
     inert with it under an open root overlay (one shared read): a banner's link or the update bar's
     Refresh is as reachable under a preview as a tile would be. -->
<app-demo-banner [attr.inert]="previewOverlayOpen() ? '' : null"></app-demo-banner>
<app-subscription-banner [attr.inert]="previewOverlayOpen() ? '' : null"></app-subscription-banner>
<app-trial-banner [attr.inert]="previewOverlayOpen() ? '' : null"></app-trial-banner>
<app-temporal-banner [attr.inert]="previewOverlayOpen() ? '' : null"></app-temporal-banner>
<p-toast position="bottom-left">
  <!-- Headless is the ONLY per-toast-dismissable template PrimeNG offers (the message template
       never receives closeFn), and it replaces the whole message body — so the faces component
       reproduces the stock face for every ordinary toast and adds the action face (Undo-class)
       for messages raised with a data.action. -->
  <ng-template pTemplate="headless" let-message let-closeFn="closeFn">
    <app-toast-faces [message]="message" [closeFn]="closeFn" />
  </ng-template>
</p-toast>
<app-file-preview></app-file-preview>
<!-- The mount ORDER of both overlays and the reference is LOAD-BEARING: each registers its
     document key listener at creation, so the file preview's and the article overlay's listeners
     run FIRST — they see the open reference and stand their Escape down, then the reference's own
     listener closes the panel; a second Escape reaches the overlay. Mounted after the reference,
     ONE Escape would collapse both layers. -->
<app-article-preview></app-article-preview>
<app-shortcut-help-panel></app-shortcut-help-panel>
<app-access-panel></app-access-panel>
<app-drive-picker-panel></app-drive-picker-panel>
<app-cloud-import-panel></app-cloud-import-panel>
<app-command-palette></app-command-palette>
<app-add-to-collection-dialog></app-add-to-collection-dialog>
<app-share-dialog></app-share-dialog>
<app-license-picker-dialog></app-license-picker-dialog>
<app-provenance-dialog></app-provenance-dialog>
<app-compliance-check-dialog></app-compliance-check-dialog>
<app-license-warn-dialog></app-license-warn-dialog>
<!-- The unsaved-work leave prompt: raised by the package prompt service on the ROOT
     ConfirmationService. Keyed, so keyless mounts riding the same root instance never
     double-render a leave raise; the extra class marks this as the package's own prompt so
     the host-prompt probe never mistakes it for a dialog's local discard prompt. -->
<p-confirmDialog [key]="UNSAVED_PROMPT_KEY" styleClass="discard-confirm unsaved-leave-prompt" />
<app-tour-overlay></app-tour-overlay>
@if (connectivity.state() === 'offline') {
  <div class="connectivity-bar offline" [attr.inert]="previewOverlayOpen() ? '' : null">
    <fa-duotone-icon [icon]="faWifiSlash"></fa-duotone-icon>
    <span>{{ 'connectivity.offline' | translate }}</span>
  </div>
} @else if (connectivity.state() === 'server-unreachable') {
  <div class="connectivity-bar server-unreachable" [attr.inert]="previewOverlayOpen() ? '' : null">
    <fa-duotone-icon [icon]="faServer"></fa-duotone-icon>
    <span>{{ 'connectivity.serverUnreachable' | translate }}</span>
  </div>
} @else if (versionCheck.updateRequired()) {
  <!-- The server no longer speaks to the bundle this tab runs: the same bar, no dismiss — Refresh is the one door. -->
  <div class="connectivity-bar update-available" [attr.inert]="previewOverlayOpen() ? '' : null">
    <fa-duotone-icon [icon]="faArrowsRotate"></fa-duotone-icon>
    <span>{{ 'update.required' | translate }}</span>
    <button class="bar-action-button" (click)="versionCheck.refresh()">{{ 'update.refresh' | translate }}</button>
  </div>
} @else if (versionCheck.updateAvailable()) {
  <div class="connectivity-bar update-available" [attr.inert]="previewOverlayOpen() ? '' : null">
    <fa-duotone-icon [icon]="faArrowsRotate"></fa-duotone-icon>
    <span>{{ 'update.available' | translate }}</span>
    <button class="bar-action-button" (click)="versionCheck.refresh()">{{ 'update.refresh' | translate }}</button>
    <button class="bar-dismiss-button" (click)="versionCheck.dismiss()">
      <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
    </button>
  </div>
} @else if (connectivity.showBackOnline()) {
  <div class="connectivity-bar online" [attr.inert]="previewOverlayOpen() ? '' : null">
    <fa-duotone-icon [icon]="connectivity.recoveredFrom() === 'server-unreachable' ? faServer : faWifi"></fa-duotone-icon>
    <span>{{ (connectivity.recoveredFrom() === 'server-unreachable' ? 'connectivity.serverReconnected' : 'connectivity.backOnline') | translate }}</span>
  </div>
}
<!-- Inert while one of the two ROOT overlays above is open: nothing behind it is reachable by keyboard
     or assistive tech, by any road (those overlays mount above, outside this element, so they stay
     live). The share and portal pages' own article overlay mounts inside the outlet, so it never keys
     this binding — it would go inert with the page. -->
<div class="app-layout" [attr.inert]="previewOverlayOpen() ? '' : null">
  <app-topbar></app-topbar>
  <div class="app-body" appBodyOffset>
    @if (showSidebar()) {
      <app-sidebar></app-sidebar>
    }
    <div class="app-main">
      <main class="app-content">
          <router-outlet></router-outlet>
      </main>
      <app-collection-dock></app-collection-dock>
    </div>
  </div>
</div>
}
